/**
 * @finora/types
 *
 * Jedini izvor istine za enume i DTO oblike koji se dele između
 * backend-a (apps/api) i frontenda (apps/web). Ne dupliraj ove
 * definicije lokalno - uvek uvezi odavde.
 *
 * Novčani iznosi su ovde tipizirani kao `string` (decimalni zapis,
 * npr. "1234.50"), jer se preko HTTP/JSON granice ionako serijalizuju
 * kao tekst/broj bez garancije preciznosti. Backend interno koristi
 * Decimal (vidi apps/api/src/common/money), a frontend string
 * pretvara u prikaz preko formatMoney() pomoćne funkcije.
 */

// ---------------------------------------------------------------------------
// Enumi
// ---------------------------------------------------------------------------
//
// NAMERNO nisu TypeScript `enum` deklaracije, već `const` objekat + izveden
// union tip (isti obrazac koji Prisma Client sam generiše za svoje enume).
// TS `enum` je NOMINALNI tip - vrednost iz Prisma-inog generisanog enuma
// (npr. $Enums.FiscalDocumentType) se ne bi mogla dodeliti promenljivoj
// tipiziranoj našim `enum`-om čak i kad su string vrednosti identične, što
// pravi lažne TS greške svuda gde mapiramo Prisma redove u DTO oblike.
// Sintaksa korišćenja (`FiscalDocumentType.DRAFT`, `Object.values(...)`,
// `z.nativeEnum(...)`) ostaje identična kao sa pravim enum-om.

export const FiscalDocumentType = {
  FISCAL_RECEIPT: "FISCAL_RECEIPT",
  INVOICE: "INVOICE",
  PROFORMA: "PROFORMA",
  ADVANCE_INVOICE: "ADVANCE_INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  DEBIT_NOTE: "DEBIT_NOTE",
} as const;
export type FiscalDocumentType =
  (typeof FiscalDocumentType)[keyof typeof FiscalDocumentType];

/**
 * Status životnog ciklusa fiskalnog dokumenta (računa).
 * Redosled ispod odgovara uobičajenom "srećnom putu"; FAILED i CANCELLED
 * su terminalna stanja greške/otkazivanja, REFUNDED je terminalno stanje
 * nakon uspešne fiskalizacije.
 */
export const FiscalDocumentStatus = {
  DRAFT: "DRAFT",
  PAYMENT_PENDING: "PAYMENT_PENDING",
  READY_FOR_FISCALIZATION: "READY_FOR_FISCALIZATION",
  FISCALIZATION_PENDING: "FISCALIZATION_PENDING",
  FISCALIZED: "FISCALIZED",
  FISCALIZATION_FAILED: "FISCALIZATION_FAILED",
  CANCELLED: "CANCELLED",
  REFUNDED: "REFUNDED",
} as const;
export type FiscalDocumentStatus =
  (typeof FiscalDocumentStatus)[keyof typeof FiscalDocumentStatus];

export const PaymentMethod = {
  CASH: "CASH",
  CARD: "CARD",
  BANK_TRANSFER: "BANK_TRANSFER",
  VOUCHER: "VOUCHER",
  COMBINED: "COMBINED",
  /** IPS / instant plaćanje (NBS IPS QR) - PFR paymentType 6 "Инстант плаћање" (27.09.2026). */
  INSTANT_PAYMENT: "INSTANT_PAYMENT",
  /** Ček - PFR paymentType 3. */
  CHECK: "CHECK",
  /** Drugo bezgotovinsko - PFR paymentType 0. */
  OTHER: "OTHER",
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const FiscalizationAttemptStatus = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
} as const;
export type FiscalizationAttemptStatus =
  (typeof FiscalizationAttemptStatus)[keyof typeof FiscalizationAttemptStatus];

export const EInvoiceDirection = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND",
} as const;
export type EInvoiceDirection =
  (typeof EInvoiceDirection)[keyof typeof EInvoiceDirection];

export const EInvoiceStatus = {
  DRAFT: "DRAFT",
  SENT: "SENT",
  PENDING_DECISION: "PENDING_DECISION",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
} as const;
export type EInvoiceStatus = (typeof EInvoiceStatus)[keyof typeof EInvoiceStatus];

export const CompanyUserRole = {
  OWNER: "OWNER",
  ADMIN: "ADMIN",
  CASHIER: "CASHIER",
  ACCOUNTANT: "ACCOUNTANT",
  VIEWER: "VIEWER",
} as const;
export type CompanyUserRole =
  (typeof CompanyUserRole)[keyof typeof CompanyUserRole];

/**
 * RBAC - granularne dozvole korisnika (vidi razgovor "RBAC - uloge i
 * podesive dozvole korisnika"). Ovo je NAMERNO ravan spisak string
 * ključeva (ne enum-po-modulu) - novi ključ se dodaje ovde bez promene
 * arhitekture guard-a/tabele. OWNER je UVEK dozvoljen nezavisno od ove
 * liste (vidi PermissionsGuard) - nema override red za OWNER.
 *
 * `ROLE_DEFAULT_PERMISSIONS` (apps/api/src/modules/auth/permissions.constants.ts)
 * definiše polaznu dozvolu po roli; `CompanyUserPermission` (Prisma model)
 * čuva INDIVIDUALNI override po korisniku koji ima prednost nad role
 * default-om.
 */
export const PERMISSION_KEYS = [
  "fiscalize",
  "refund",
  "close_advance",
  "products.view",
  "products.create",
  "products.edit",
  "products.delete",
  "products.change_tax_rate",
  "products.import",
  "pfr.view",
  "pfr.settings",
  "pfr.certificate",
  "company.view",
  "company.edit",
  "users.view",
  "users.create",
  "users.edit",
  "users.delete",
  "users.permissions",
  // Print Agent (Finora kasa štampa preko lokalnog Windows agenta - vidi
  // "Finora - Print Agent API" specifikaciju) - "view" dozvoljava kasi da
  // pronađe agenta i preuzme auth_token (GET /print-agents,
  // /print-agents/:id/connection), "manage" dozvoljava administraciju
  // (licencni ključevi, preimenovanje, opoziv agenta).
  "print_agent.view",
  "print_agent.manage",
  // Knjizenje troskova (05.10.2026): samo vlasnik i knjigovodja.
  "expenses.create",
  // E-arhiv (16.10.2026): uređuju vlasnik i knjigovođa, ostali samo pregled.
  "earhiv.manage",
  // WISP (27.10.2026): pretplatnici, paketi, ruteri, suspenzija/aktivacija. Podrazumevano samo vlasnik
  // (matrica uloga se ne menja bez odluke vlasnika); ostalima se dodeljuje pojedinačno.
  "wisp.manage",
] as const;
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const EntityLifecycleStatus = {
  ACTIVE: "ACTIVE",
  ARCHIVED: "ARCHIVED",
} as const;
export type EntityLifecycleStatus =
  (typeof EntityLifecycleStatus)[keyof typeof EntityLifecycleStatus];

// ---------------------------------------------------------------------------
// Zajedničke pomoćne strukture
// ---------------------------------------------------------------------------

/** Standardni format API greške - vidi apps/api/src/common/filters. */
export interface ApiErrorResponse {
  statusCode: number;
  /** Stabilan mašinski čitljiv kod greške, npr. "FISCAL_DOCUMENT_EMPTY". */
  errorCode: string;
  /** Poruka razumljiva korisniku, na srpskom. */
  message: string;
  /** Detalji validacije po polju, ako postoje. */
  fieldErrors?: Record<string, string[]>;
  path: string;
  timestamp: string;
  /**
   * Prisutno SAMO kad je errorCode "MODULE_NOT_AVAILABLE" (ModuleAccessGuard/
   * EntitlementService) - frontend ovo koristi da ponudi CTA "Aktiviraj
   * dodatak" umesto sirove poruke, vidi ModuleGateNotice komponentu.
   */
  moduleAccess?: {
    addonAvailable?: boolean;
    addonPrice?: string;
    minimumPackage?: { id: string; key: string; name: string } | null;
    requiredModules?: { key: string; name: string }[];
  };
  /**
   * Prisutno SAMO na 409 iz UsersService.assignCard (NFC kartica vec
   * AKTIVNA kod drugog zaposlenog) - ime tog zaposlenog, "Dopuna
   * specifikacije" 25.09.2026.
   */
  assignedTo?: string;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Domenski entiteti (oblik onoga što API vraća)
// ---------------------------------------------------------------------------

export interface CompanyDto {
  id: string;
  name: string;
  slug: string;
  taxId: string;
  registrationNumber: string;
  defaultCurrency: string;
  esirId?: string | null;
}

export const SkuFormat = {
  NUMERIC: "NUMERIC",
  ALPHANUMERIC: "ALPHANUMERIC",
} as const;
export type SkuFormat = (typeof SkuFormat)[keyof typeof SkuFormat];

export const EmailTemplateDesign = {
  NONE: "NONE",
  DESIGN_1: "DESIGN_1",
  DESIGN_2: "DESIGN_2",
} as const;
export type EmailTemplateDesign = (typeof EmailTemplateDesign)[keyof typeof EmailTemplateDesign];

export const PosQuantityMode = {
  WHOLE: "WHOLE",
  DECIMAL: "DECIMAL",
} as const;
export type PosQuantityMode = (typeof PosQuantityMode)[keyof typeof PosQuantityMode];

export interface CompanySettingsDto extends CompanyDto {
  businessLocationId: string;
  businessLocationName: string;
  businessLocationAddress: string;
  isVatRegistered: boolean;
  showProductImagesOnQuotes: boolean;
  showProductImagesOnInvoices: boolean;
  bankAccount?: string | null;
  skuFormat: SkuFormat;
  invoiceNumberPrefix: string;
  quoteNumberPrefix: string;
  deliveryNoteNumberPrefix: string;
  includeYearInNumber: boolean;
  invoiceNumberStart?: number | null;
  quoteNumberStart?: number | null;
  showLogoOnReceipt: boolean;
  posQuantityMode: PosQuantityMode;
  bankAccountIban?: string | null;
  emailTemplateDesign: EmailTemplateDesign;
  logoUrl?: string | null;
  quoteAcceptanceText?: string | null;
  defaultQuoteNote?: string | null;
  defaultQuotePaymentTerms?: string | null;
  defaultInvoiceNote?: string | null;
  defaultInvoicePaymentTerms?: string | null;
  defaultDeliveryNoteNote?: string | null;
  autoSendToSefOnAcceptance: boolean;
  defaultCombinedPaymentMethod: PaymentMethod;
  usesWarehouse: boolean;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  phone?: string | null;
}

export interface UpdateCompanySettingsPayload {
  name?: string;
  taxId?: string;
  registrationNumber?: string;
  businessLocationName?: string;
  businessLocationAddress?: string;
  isVatRegistered?: boolean;
  showProductImagesOnQuotes?: boolean;
  showProductImagesOnInvoices?: boolean;
  bankAccount?: string;
  skuFormat?: SkuFormat;
  invoiceNumberPrefix?: string;
  quoteNumberPrefix?: string;
  deliveryNoteNumberPrefix?: string;
  includeYearInNumber?: boolean;
  invoiceNumberStart?: number | null;
  quoteNumberStart?: number | null;
  showLogoOnReceipt?: boolean;
  posQuantityMode?: PosQuantityMode;
  emailTemplateDesign?: EmailTemplateDesign;
  logoUrl?: string;
  quoteAcceptanceText?: string;
  defaultQuoteNote?: string;
  defaultQuotePaymentTerms?: string;
  defaultInvoiceNote?: string;
  defaultInvoicePaymentTerms?: string;
  defaultDeliveryNoteNote?: string;
  autoSendToSefOnAcceptance?: boolean;
  defaultCombinedPaymentMethod?: PaymentMethod;
  usesWarehouse?: boolean;
  esirId?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
}

/** Super-admin - izmena istih polja na tuđoj firmi (vidi CompanyDetailPage.tsx). */
export interface UpdateCompanyProfileAdminPayload {
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
}

export interface SmtpSettingsDto {
  hasSmtpConfig: boolean;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUser?: string | null;
  smtpFrom?: string | null;
  smtpSecure: boolean;
}

export interface UpdateSmtpSettingsPayload {
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPass?: string;
  smtpFrom?: string;
  smtpSecure?: boolean;
}

export const PfrMode = {
  SANDBOX: "SANDBOX",
  PROD: "PROD",
} as const;
export type PfrMode = (typeof PfrMode)[keyof typeof PfrMode];

export interface PfrSettingsDto {
  pfrMode: PfrMode;
  pfrBaseUrl?: string | null;
  hasCertificate: boolean;
  hasApiKey: boolean;
  jid?: string | null;
  businessLocation?: string | null;
}

export interface UpdatePfrSettingsPayload {
  pfrMode?: PfrMode;
  pfrBaseUrl?: string;
  pfrCertificatePassword?: string;
  pfrApiKey?: string;
}

/** Rezultat automatskog očitavanja PAK/lozinke iz PDF potvrde Poreske uprave (n8n parser). */
export interface PfrCertificateParseResultDto extends PfrSettingsDto {
  parsed: {
    pib: string | null;
    jid: string | null;
    businessLocation: string | null;
    downloadUrl: string | null;
    vsdcUrl: string | null;
  };
}

/** Rezultat kombinovanog otpremanja ZIP arhive sertifikata (Poreska uprava) i/ili PDF potvrde u jednom koraku. */
export interface PfrCertificateBundleResultDto extends PfrSettingsDto {
  parsed: {
    pib: string | null;
    jid: string | null;
    businessLocation: string | null;
    downloadUrl: string | null;
    vsdcUrl: string | null;
  } | null;
  /** Naziv fajla iz ZIP arhive iz kog je izvučen .p12/.pfx sertifikat (null ako ZIP nije priložen). */
  pfxFileName: string | null;
}

/**
 * Identifikacioni podaci pročitani IZ PRILOŽENOG PFX sertifikata (Subject/OID) -
 * pregled PRE upisa, vidi POST /company/fiscal-devices/preview. Puna
 * implementacija po lokaciji (25.09.2026) - svaki uređaj/kasa nosi SOPSTVENI
 * sertifikat, zato se ovde šalje fajl (multipart), ne čita se Company-level
 * sertifikat kao u ranijem (napuštenom) nacrtu ovog tipa.
 */
export interface FiscalDeviceIdentityDto {
  jid: string | null;
  businessLocationCode: string | null;
  businessLocationName: string | null;
  businessLocationRaw: string | null;
  companyName: string | null;
  commonName: string | null;
  address: string | null;
  city: string | null;
  pib: string | null;
  validFrom: string | null;
  validTo: string | null;
  fingerprintSha256: string;
}

export type FiscalDeviceStatus = "PENDING_LOCATION" | "ACTIVE" | "INACTIVE";

/** Registrovana kasa (fiskalni uređaj) - sopstven PFX/PAK po uređaju/lokaciji. */
export interface FiscalDeviceDto {
  id: string;
  jid: string | null;
  name: string | null;
  companyName: string | null;
  businessLocationCodeFromCertificate: string | null;
  businessLocationNameFromCertificate: string | null;
  pib: string | null;
  address: string | null;
  city: string | null;
  validFrom: string | null;
  validTo: string | null;
  status: FiscalDeviceStatus;
  businessLocationId: string | null;
  businessLocationName: string | null;
  fcmDeviceId: string | null;
  createdAt: string;
}

/** Polja uz .pfx fajl pri registraciji (multipart - vidi POST /company/fiscal-devices). */
export interface RegisterFiscalDeviceInput {
  password?: string;
  apiKey: string;
  businessLocationId?: string | null;
  fcmDeviceId?: string | null;
  name?: string | null;
}

export interface CompanyFcmDeviceDto {
  id: string;
  userName: string;
  platform: string;
  deviceId: string | null;
  lastActiveAt: string;
}

/** Neuspešno spojen artikal iz JSON izvoza Poreske uprave (nema odgovarajući artikal u Finora bazi). */
export interface PfrReceiptsUnmatchedItemDto {
  itemId: string;
  invoiceNumber: string;
  gtin: string | null;
  name: string;
  quantity: number;
  totalAmount: number;
}

export interface MapPfrReceiptItemInput {
  name: string;
  productId: string;
}

export interface MapPfrReceiptItemResultDto {
  updatedItemsCount: number;
  product: { id: string; sku: string };
}

export interface PfrReceiptsConvertResultDto {
  importId: string;
  base64: string;
  fileName: string;
  itemCount: number;
  receiptCount: number;
  matchedCount: number;
  unmatchedCount: number;
  unmatched: PfrReceiptsUnmatchedItemDto[];
}

export interface PfrReceiptsImportSummaryDto {
  id: string;
  fileName: string | null;
  receiptCount: number;
  itemCount: number;
  matchedCount: number;
  unmatchedCount: number;
  createdAt: string;
}

export type PfrReceiptsImportItemStatus = "MATCHED" | "UNMATCHED";

export interface PfrReceiptsImportItemDto {
  id: string;
  invoiceNumber: string | null;
  gtin: string | null;
  /** Trenutni (novi) naziv artikla iz Finora baze ako je spojeno, inače isto kao originalName. */
  name: string;
  /** Originalni naziv kako ga je poslala Poreska uprava (iz kase) - za poređenje ako je mapiranje pogrešno. */
  originalName: string;
  quantity: string;
  unitPrice: string;
  totalAmount: string;
  matchedSku: string | null;
  matchMethod: "barcode" | "mapping" | "name" | null;
  status: PfrReceiptsImportItemStatus;
}

export interface PfrReceiptsImportDetailDto extends PfrReceiptsImportSummaryDto {
  items: PfrReceiptsImportItemDto[];
}

export interface PfrReceiptMappingSuggestionProductDto {
  id: string;
  sku: string;
  name: string;
}

/**
 * Predlog za "Mapiraj" modal - da li je ovaj naziv već ranije ručno mapiran
 * (da se vidi/ispravi eventualna greška) i do 5 artikala iz baze najsličnijih
 * nazivu iz JSON-a, plus predložena početna reč za pretragu.
 */
export interface PfrReceiptMappingSuggestionDto {
  existingMapping: { productId: string; sku: string; name: string; rawName: string } | null;
  suggestions: PfrReceiptMappingSuggestionProductDto[];
  suggestedSearchTerm: string;
}

export interface IpsPgwSettingsDto {
  ipsPgwEnabled: boolean;
  ipsPgwEnvironment: "TEST" | "PRODUCTION";
  ipsPgwTid?: string | null;
  ipsPgwMcc?: string | null;
  hasIpsPgwUserId: boolean;
  ipsPgwUserIdMasked?: string | null;
}

export interface UpdateIpsPgwSettingsPayload {
  ipsPgwEnabled?: boolean;
  ipsPgwEnvironment?: "TEST" | "PRODUCTION";
  ipsPgwTid?: string;
  ipsPgwUserId?: string;
  ipsPgwMcc?: string;
}

export interface OtpCardSettingsDto {
  otpCardEnabled: boolean;
  otpCardEnvironment: "TEST" | "PRODUCTION";
  otpCardStoreName?: string | null;
  otpCardCurrency: string;
  hasOtpCardSharedSecret: boolean;
  hasOtpCardSharedSecretApi: boolean;
}

export interface UpdateOtpCardSettingsPayload {
  otpCardEnabled?: boolean;
  otpCardEnvironment?: "TEST" | "PRODUCTION";
  otpCardStoreName?: string;
  otpCardCurrency?: string;
  otpCardSharedSecret?: string;
  otpCardSharedSecretApi?: string;
}

export interface OtpCardTransactionDto {
  id: string;
  salesDocumentId: string;
  amount: string;
  status: "PENDING" | "SUCCESS" | "FAILED";
  resultCode?: string | null;
  authNumber?: string | null;
  bankTransactionId?: string | null;
  maskedPan?: string | null;
  cardType?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntesaCardSettingsDto {
  intesaCardEnabled: boolean;
  intesaCardEnvironment: "TEST" | "PRODUCTION";
  intesaCardClientId?: string | null;
  intesaCardCurrency: string;
  hasIntesaCardStoreKey: boolean;
}

export interface UpdateIntesaCardSettingsPayload {
  intesaCardEnabled?: boolean;
  intesaCardEnvironment?: "TEST" | "PRODUCTION";
  intesaCardClientId?: string;
  intesaCardStoreKey?: string;
  intesaCardCurrency?: string;
}

export interface IntesaCardTransactionDto {
  id: string;
  salesDocumentId: string;
  amount: string;
  status: "PENDING" | "SUCCESS" | "FAILED";
  response?: string | null;
  procReturnCode?: string | null;
  authCode?: string | null;
  transId?: string | null;
  maskedPan?: string | null;
  cardBrand?: string | null;
  errMsg?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ===== Kurirske sluzbe (04.10.2026) =====

export type CourierProvider = "BEX" | "POST_EXPRESS" | "AKS" | "D_EXPRESS" | "CITY_EXPRESS";
export type ShipmentStatus = "PENDING" | "CREATED" | "FAILED" | "CANCELLED";
export type CourierLabelFormat = "THERMAL" | "A4";

export interface CourierListItemDto {
  code: CourierProvider;
  label: string;
  /** Da li Finora ima stvarnu API integraciju za ovog kurira (trenutno samo BEX). */
  implemented: boolean;
  /** implemented I korisnik je uneo/ukljucio podesavanja. */
  enabled: boolean;
  supportsA4: boolean;
}

export interface ShipmentDto {
  id: string;
  warehouseIssueOrderId: string;
  courier: CourierProvider;
  courierLabel: string;
  status: ShipmentStatus;
  trackingNumber?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  trackingUrl?: string | null;
  deliveryState?: string | null;
  deliveryStateLabel?: string | null;
  deliveryStatusText?: string | null;
  lastStatusAt?: string | null;
  lastSyncedAt?: string | null;
  deliveredAt?: string | null;
  canRefresh?: boolean;
  orderNumber?: string | null;
  customerName?: string | null;
  events?: ShipmentTrackingEventDto[];
}

export interface DispatchShipmentPayload {
  weightKg?: number;
  totalPackages?: number;
  codAmount?: number;
  comment?: string;
}

export interface BexCourierSettingsDto {
  bexEnabled: boolean;
  bexClientId?: string | null;
  bexSenderAccount?: string | null;
  bexSenderPhone?: string | null;
  bexSenderContactPerson?: string | null;
  hasBexApiKey: boolean;
}

export interface UpdateBexCourierSettingsPayload {
  bexEnabled?: boolean;
  bexApiKey?: string;
  bexClientId?: string;
  bexSenderAccount?: string;
  bexSenderPhone?: string;
  bexSenderContactPerson?: string;
}

export interface CityExpressCourierSettingsDto {
  cityExpressEnabled: boolean;
  cityExpressSenderId?: string | null;
  hasCityExpressApiKey: boolean;
  cityExpressWebhookUrl: string;
}

export interface ShipmentNotificationSettingsDto {
  notifyOnDispatch: boolean;
  notifyOnStatus: boolean;
}

export interface ShipmentTrackingEventDto {
  code: string;
  description: string;
  occurredAt: string;
}

export interface UpdateCityExpressCourierSettingsPayload {
  cityExpressEnabled?: boolean;
  cityExpressApiKey?: string;
  cityExpressSenderId?: string;
}

export interface PostaCourierSettingsDto {
  postaEnabled: boolean;
  postaEnvironment: string;
  postaUsername?: string | null;
  postaSenderAccount?: string | null;
  postaSenderPhone?: string | null;
  postaSenderContactPerson?: string | null;
  hasPostaPassword: boolean;
}

export interface UpdatePostaCourierSettingsPayload {
  postaEnabled?: boolean;
  postaEnvironment?: string;
  postaUsername?: string;
  postaPassword?: string;
  postaSenderAccount?: string;
  postaSenderPhone?: string;
  postaSenderContactPerson?: string;
}

export interface RaiffeisenCardSettingsDto {
  raiffeisenCardEnabled: boolean;
  raiffeisenCardEnvironment: "TEST" | "PRODUCTION";
  raiffeisenCardMerchantId?: string | null;
  raiffeisenCardTerminalId?: string | null;
  raiffeisenCardCurrency: string;
  hasRaiffeisenCardPrivateKey: boolean;
  hasRaiffeisenCardBankCert: boolean;
}

export interface UpdateRaiffeisenCardSettingsPayload {
  raiffeisenCardEnabled?: boolean;
  raiffeisenCardEnvironment?: "TEST" | "PRODUCTION";
  raiffeisenCardMerchantId?: string;
  raiffeisenCardTerminalId?: string;
  raiffeisenCardPrivateKeyPem?: string;
  raiffeisenCardBankCertPem?: string;
  raiffeisenCardCurrency?: string;
}

export interface RaiffeisenCardTransactionDto {
  id: string;
  salesDocumentId: string;
  amount: string;
  status: "PENDING" | "SUCCESS" | "FAILED";
  tranCode?: string | null;
  approvalCode?: string | null;
  rrn?: string | null;
  xid?: string | null;
  proxyPan?: string | null;
  cardType?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessLocationDto {
  id: string;
  companyId: string;
  name: string;
  address: string;
  isDefault: boolean;
  status: EntityLifecycleStatus;
}

export interface UserSummaryDto {
  id: string;
  fullName: string;
  email: string;
}

export interface CustomerDto {
  id: string;
  companyId: string;
  name: string;
  taxId?: string | null;
  registrationNumber?: string | null;
  jbkjs?: string | null;
  isVatRegistered: boolean;
  hasEInvoice: boolean;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  status: EntityLifecycleStatus;
  externalId?: string | null;
  integrationId?: string | null;
  buyerIdType?: string | null;
  buyerIdValue?: string | null;
}

export interface CreateCustomerPayload {
  name: string;
  taxId?: string | null;
  registrationNumber?: string | null;
  jbkjs?: string | null;
  isVatRegistered?: boolean;
  hasEInvoice?: boolean;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  buyerIdType?: string | null;
  buyerIdValue?: string | null;
}

/** Rezultat pretrage mesta po poštanskom broju (MiniMax API) - vidi razgovor "Faza 3". */
export interface PostalCodeLookupDto {
  postalCode: string;
  city: string;
}

export type UpdateCustomerPayload = Partial<CreateCustomerPayload>;

export interface ProductCategoryDto {
  id: string;
  companyId: string;
  name: string;
  colorHex?: string | null;
  imageUrl?: string | null;
  sortOrder: number;
}

export interface TaxRateDto {
  id: string;
  companyId: string;
  name: string;
  /** Procenat kao decimalni string, npr. "20.00" za 20%. */
  ratePercent: string;
  isDefault: boolean;
  isVatExempt: boolean;
}

export const WarrantyUnit = {
  YEARS: "YEARS",
  MONTHS: "MONTHS",
} as const;
export type WarrantyUnit = (typeof WarrantyUnit)[keyof typeof WarrantyUnit];

// Dogovoreno 28.09.2026, modul Artikli (varijacije + rok trajanja).
export const ShelfLifeMode = {
  NONE: "NONE",
  DATE: "DATE",
  DAYS: "DAYS",
} as const;
export type ShelfLifeMode = (typeof ShelfLifeMode)[keyof typeof ShelfLifeMode];

export interface ProductVariationDto {
  id: string;
  productId: string;
  name: string;
  priceModifier: string;
  barcode: string | null;
  sku: string | null;
  status: EntityLifecycleStatus;
  sortOrder: number;
}

export interface ProductRemovableIngredientDto {
  id: string;
  productId: string;
  name: string;
  sortOrder: number;
}

export interface ProductAttributeOptionDto {
  id: string;
  groupId: string;
  name: string;
  isFree: boolean;
  priceModifier: string;
  imageUrl: string | null;
  /** Modul Normativ: namirnica i količina koju dodatak troši (null = ništa ne skida). */
  ingredientProductId?: string | null;
  ingredientQuantity?: string | null;
}

export interface ProductAttributeGroupDto {
  id: string;
  productId: string;
  name: string;
  options: ProductAttributeOptionDto[];
}

export interface ProductDto {
  id: string;
  /** Modul Normativ: namirnica se ne prodaje (ne ide na kasu), ali ulazi u lager i normativ. */
  isIngredient?: boolean;
  /** Grupe dodataka sa vrednostima (ista struktura kao GET /products/:id/attribute-groups). */
  attributeGroups?: ProductAttributeGroupDto[];
  companyId: string;
  categoryId: string | null;
  /** Nested za pogodnost potrošača (isti obrazac kao taxRate ispod) - npr. budući POS/kasa ekran. */
  category?: { id: string; name: string; colorHex: string | null } | null;
  name: string;
  sku: string;
  barcode?: string | null;
  description?: string | null;
  /** Jedinična cena SA porezom, decimalni string. */
  unitPriceGross: string;
  taxRateId: string;
  taxRate?: TaxRateDto;
  unit: string;
  isService: boolean;
  status: EntityLifecycleStatus;
  purchasePrice?: string | null;
  purchaseDiscountPercent?: string | null;
  /** Izračunato: purchasePrice * (1 - purchaseDiscountPercent/100). Samo za internu upotrebu (marža). */
  effectivePurchasePrice?: string | null;
  imageUrl?: string | null;
  hasWarranty: boolean;
  warrantyDuration?: number | null;
  warrantyUnit: WarrantyUnit;

  // --- Dogovoreno 28.09.2026, modul Artikli (varijacije + rok trajanja) ---
  internalNote: string | null;
  isFeatured: boolean;
  allowDiscount: boolean;
  showImageOnReceipt: boolean;
  shelfLifeMode: ShelfLifeMode;
  shelfLifeDate: string | null;
  shelfLifeDays: number | null;
  minStockQuantity: string | null;
  ignoreStockLimit: boolean;
  metaTitle: string | null;
  shortDescription: string | null;
  tags: string[];
  sortOrder: number;
  variations: ProductVariationDto[];
  removableIngredients: ProductRemovableIngredientDto[];
}

export interface CreateProductPayload {
  name: string;
  sku?: string;
  barcode?: string | null;
  description?: string | null;
  unitPriceGross: string;
  taxRateId: string;
  categoryId?: string | null;
  unit?: string;
  isService?: boolean;
  purchasePrice?: string | null;
  purchaseDiscountPercent?: string | null;
  hasWarranty?: boolean;
  warrantyDuration?: number | null;
  warrantyUnit?: WarrantyUnit;
  internalNote?: string | null;
  isFeatured?: boolean;
  allowDiscount?: boolean;
  showImageOnReceipt?: boolean;
  shelfLifeMode?: ShelfLifeMode;
  shelfLifeDate?: string | null;
  shelfLifeDays?: number | null;
  minStockQuantity?: string | null;
  ignoreStockLimit?: boolean;
  metaTitle?: string | null;
  shortDescription?: string | null;
  tags?: string[];
  sortOrder?: number;
}

export type UpdateProductPayload = Partial<CreateProductPayload>;

export interface UnitDto {
  id: string;
  companyId: string;
  name: string;
}

export interface FiscalDocumentItemDto {
  id: string;
  fiscalDocumentId: string;
  productId: string | null;
  name: string;
  quantity: string;
  unit: string;
  taxLabel?: string | null;
  unitPriceGross: string;
  taxRatePercent: string;
  discountAmount: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
}

export interface FiscalDocumentTaxBreakdownDto {
  taxRatePercent: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
}

export interface PaymentDto {
  id: string;
  fiscalDocumentId: string;
  method: PaymentMethod;
  amount: string;
  receivedAmount?: string | null;
  changeAmount?: string | null;
  createdAt: string;
}

export interface FiscalizationAttemptDto {
  id: string;
  fiscalDocumentId: string;
  status: FiscalizationAttemptStatus;
  attemptNumber: number;
  providerMessage?: string | null;
  fiscalNumber?: string | null;
  createdAt: string;
}

export interface FiscalDocumentDto {
  id: string;
  companyId: string;
  businessLocationId: string;
  type: FiscalDocumentType;
  status: FiscalDocumentStatus;
  documentNumber: string | null;
  customerId: string | null;
  customer?: CustomerDto | null;
  buyerPib?: string | null;
  buyerName?: string | null;
  buyerIdType?: string | null;
  /** Adresa kupca bez punog Customer zapisa - vidi napomenu na
   * FiscalDocument.buyerAddress u schema.prisma. */
  buyerAddress?: string | null;
  /** Opciono polje kupca (npr. "20:01234567") - samo Promet Prodaja. */
  buyerOptionalField?: string | null;
  currency: string;
  items: FiscalDocumentItemDto[];
  taxBreakdown: FiscalDocumentTaxBreakdownDto[];
  discountAmount: string;
  /** Popust SAMO na nivou dokumenta (24.09.2026, korisnički zahtev) - za
   * razliku od `discountAmount` (koji je ZBIR popusta po stavkama I ovog
   * dokument-nivo popusta, vidi mapFiscalDocument()/calculateDocumentTotals),
   * ovo je sirova vrednost koju je kasir uneo preko `PATCH :id`
   * (`discountAmount` u telu zahteva) - kasa ovo koristi za round-trip
   * prikaza/izmene polja "Popust na račun", da ne bi slučajno upisala
   * kombinovan iznos nazad kao da je samo dokument-nivo popust. */
  documentDiscountAmount: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  paidAmount: string;
  dueAmount: string;
  note?: string | null;
  isAdvance?: boolean;
  pfrDocumentType?: "PROMET" | "AVANS" | "OBUKA" | "KOPIJA" | "PREDRACUN";
  payments: PaymentDto[];
  fiscalizationAttempts: FiscalizationAttemptDto[];
  fiscalNumber?: string | null;
  /** Ko je potpisao racun - V_PFR (Finora preko provajdera) ili L_PFR
   * (lokalni uredjaj kod kase - vidi docs/timovi/esir-lpfr-integracija.md,
   * 01.10.2026). Potrebno izvestajima/kopijama; storno/kopija L-PFR racuna
   * mora ici kroz isti L-PFR, ne V-PFR. */
  fiscalProcessor?: "V_PFR" | "L_PFR";
  /** JID L-PFR uredjaja koji je potpisao racun (samo za fiscalProcessor=L_PFR) - vidi esir-lpfr-integracija.md, stavka (3), tacka 4. */
  lpfrJid?: string | null;
  fiscalizedAt?: string | null;
  qrCodeBase64?: string | null;
  verificationUrl?: string | null;
  invoiceCounter?: string | null;
  /** Pun tekst PFR žurnala (tačan sadržaj sa linijom "КРАЈ ФИСКАЛНОГ
   * РАЧУНА") - vidi Mobile agent zahtev 22.09.2026, potreban za Bluetooth/
   * Print Agent štampu. */
  journal?: string | null;
  refundFiscalNumber?: string | null;
  refundInvoiceCounter?: string | null;
  refundQrCodeBase64?: string | null;
  refundVerificationUrl?: string | null;
  /** Žurnal STORNO (refundacionog) računa - isto obrazloženje kao `journal`. */
  refundJournal?: string | null;
  refundedAt?: string | null;
  copyFiscalNumber?: string | null;
  copyInvoiceCounter?: string | null;
  copyQrCodeBase64?: string | null;
  copyVerificationUrl?: string | null;
  /** Žurnal KOPIJE ("Štampaj kopiju") - isto obrazloženje kao `journal`. */
  copyJournal?: string | null;
  copiedAt?: string | null;
  /** Task #38 - PFR "Референтни број"/DT na prethodni račun u avansnom
   * lancu (npr. gotovinski račun koji zatvara avans referencira Аванс
   * Рефундација) - vidi closeAdvanceWithCashSale. */
  referenceFiscalNumber?: string | null;
  referenceFiscalizedAt?: string | null;
  /** ID avansnog dokumenta čiji je ovaj "gotovinski nastavak" (samo za prikaz). */
  linkedAdvanceDocumentId?: string | null;
  /**
   * Iskorišćeni avans (razgovor 23.09.2026) - RAČUNSKO polje, NIJE kolona u
   * bazi. Popunjeno SAMO kad `linkedAdvanceDocumentId` postoji i taj
   * povezani Avans-Prodaja dokument je zaista validno zatvoren (status
   * REFUNDED) - servis ga izračunava iz `grossAmount` tog dokumenta.
   * Koristi se na kasi da se od `grossAmount` ovog (konačnog) dokumenta
   * oduzme već pokriven iznos i kasir naplati samo doplatu.
   */
  advanceCreditAmount?: string | null;
  createdBy?: string | null;
  /** Ime kasira koje je kasa poslala preko Cloud API-ja (inače se uzima iz korisnika koji je izdao račun). */
  cashierName?: string | null;
  externalId?: string | null;
  integrationId?: string | null;
  buyerId?: string | null;
  /** Serijski broj opreme (03.10.2026, MerchantPro s/n:... napomena) -
   * NIKAD deo buyerId poslatog na PFR (PFR odbija fiskalizaciju ako se tu
   * nadje), samo se prikazuje ekranu/PDF-u u reklamnom prostoru ispod
   * zvanicnog zavrsetka racuna - vidi resolveBuyerId() u
   * fiscal-document.mapper.ts. */
  buyerIdExtra?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EInvoiceDto {
  id: string;
  companyId: string;
  direction: EInvoiceDirection;
  status: EInvoiceStatus;
  counterpartyName: string;
  documentNumber: string;
  issueDate: string;
  grossAmount: string;
  currency: string;
}

// ---------------------------------------------------------------------------
// FAZA 2: Ponude i fakture (SalesDocument)
// ---------------------------------------------------------------------------

export const SalesDocumentType = {
  QUOTE: "QUOTE",
  PROFORMA_INVOICE: "PROFORMA_INVOICE",
  INVOICE: "INVOICE",
  ADVANCE_INVOICE: "ADVANCE_INVOICE",
  FINAL_INVOICE: "FINAL_INVOICE",
  CREDIT_NOTE: "CREDIT_NOTE",
  DEBIT_NOTE: "DEBIT_NOTE",
} as const;
export type SalesDocumentType =
  (typeof SalesDocumentType)[keyof typeof SalesDocumentType];

export const SalesDocumentStatus = {
  DRAFT: "DRAFT",
  APPROVED: "APPROVED",
  SENT: "SENT",
  VIEWED: "VIEWED",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED",
  CONVERTED: "CONVERTED",
  PARTIALLY_PAID: "PARTIALLY_PAID",
  PAID: "PAID",
  OVERDUE: "OVERDUE",
  CANCELLED: "CANCELLED",
} as const;
export type SalesDocumentStatus =
  (typeof SalesDocumentStatus)[keyof typeof SalesDocumentStatus];

// ---------------------------------------------------------------------------
// Ponavljajuće fakture
// ---------------------------------------------------------------------------

export const RecurringFrequency = {
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
  YEARLY: "YEARLY",
} as const;
export type RecurringFrequency =
  (typeof RecurringFrequency)[keyof typeof RecurringFrequency];

export interface RecurringInvoiceTemplateItemDto {
  id: string;
  productId: string | null;
  name: string;
  quantity: string;
  unitPriceGross: string;
  taxRateId: string;
  taxRatePercent: string;
  discountAmount: string;
}

export interface RecurringInvoiceTemplateDto {
  id: string;
  companyId: string;
  businessLocationId: string;
  customerId: string;
  customer?: CustomerDto;
  frequency: RecurringFrequency;
  startDate: string;
  endDate: string | null;
  nextRunDate: string;
  autoSend: boolean;
  active: boolean;
  paymentTerms?: string | null;
  customerNote?: string | null;
  items: RecurringInvoiceTemplateItemDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateRecurringInvoiceTemplatePayload {
  businessLocationId: string;
  customerId: string;
  frequency: RecurringFrequency;
  startDate: string;
  endDate?: string | null;
  autoSend?: boolean;
  paymentTerms?: string | null;
  customerNote?: string | null;
  items: Array<{
    productId?: string | null;
    name: string;
    quantity: string;
    unitPriceGross: string;
    taxRateId: string;
    discountAmount?: string;
  }>;
}

export interface SalesDocumentItemDto {
  id: string;
  salesDocumentId: string;
  productId: string | null;
  name: string;
  description?: string | null;
  quantity: string;
  unit: string;
  unitPriceGross: string;
  taxRatePercent: string;
  discountAmount: string;
  invoicedQuantity: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  /** Snapshot nabavne cene u trenutku dodavanja - samo za internu (staff) maržu. */
  purchasePriceGross?: string | null;
  productImageUrl?: string | null;
  serialNumber?: string | null;
}

export interface SalesDocumentPaymentDto {
  id: string;
  salesDocumentId: string;
  method: PaymentMethod;
  amount: string;
  note?: string | null;
  /** Poreklo uplate (izvod, IPS QR...) - vidi SalesDocumentPayment.metadata napomenu u šemi. */
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

/** Log pokušaja IPS QR (Payten PGW) plaćanja za jedan dokument - uspešnih i neuspešnih. */
export interface IpsPgwTransactionDto {
  id: string;
  salesDocumentId: string;
  amount: string;
  status: "PENDING" | "SUCCESS" | "FAILED";
  responseCode?: string | null;
  paymentReference?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SalesDocumentDto {
  id: string;
  companyId: string;
  businessLocationId: string;
  type: SalesDocumentType;
  status: SalesDocumentStatus;
  documentNumber: string | null;
  customerId: string | null;
  customer?: CustomerDto | null;
  currency: string;
  issueDate: string;
  validUntilOrDueDate: string | null;
  turnoverDate: string | null;
  paymentTerms?: string | null;
  deliveryTerms?: string | null;
  customerNote?: string | null;
  internalNote?: string | null;
  items: SalesDocumentItemDto[];
  taxBreakdown: FiscalDocumentTaxBreakdownDto[];
  discountAmount: string;
  shippingAmount: string;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
  paidAmount: string;
  dueAmount: string;
  invoicedAmount: string;
  sourceQuoteId: string | null;
  invoiceIds: string[];
  fiscalDocumentId: string | null;
  publicLinkToken: string | null;
  sentAt: string | null;
  viewedAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  payments: SalesDocumentPaymentDto[];
  createdAt: string;
  updatedAt: string;
  showProductImages: boolean;
  /** Samo na javnom pregledu ponude - tekst iz Podešavanja koji se prikazuje pre dugmeta za prihvatanje. */
  quoteAcceptanceText?: string | null;
  /** Samo na javnom pregledu fakture (/public/invoices/:token) - podaci firme koja je izdala fakturu. */
  issuerCompanyName?: string | null;
  bankAccount?: string | null;
  bankAccountIban?: string | null;
  ipsPgwEnabled?: boolean;
  otpCardEnabled?: boolean;
  intesaCardEnabled?: boolean;
  raiffeisenCardEnabled?: boolean;
}

/** "Moji računi" - dokumenti (ponude/fakture) koje je DRUGA Finora firma izdala trenutnoj firmi (vidi Customer.sourceCompanyId). */
export interface MyInvoicesResponseDto {
  issuerCompanyName: string | null;
  documents: SalesDocumentDto[];
}

export interface CreateSalesDocumentPayload {
  type: SalesDocumentType;
  businessLocationId: string;
  customerId?: string | null;
  currency?: string;
  validUntilOrDueDate?: string | null;
  paymentTerms?: string | null;
  deliveryTerms?: string | null;
  customerNote?: string | null;
  internalNote?: string | null;
}

export interface AddSalesDocumentItemPayload {
  productId?: string | null;
  name: string;
  description?: string | null;
  quantity: string;
  unit?: string;
  unitPriceGross: string;
  taxRateId: string;
  discountAmount?: string;
  purchasePriceGross?: string | null;
  serialNumber?: string | null;
}

export interface UpdateSalesDocumentItemPayload {
  quantity?: string;
  discountAmount?: string;
  unitPriceGross?: string;
  taxRateId?: string;
  serialNumber?: string | null;
}

export interface ConvertQuoteToInvoicePayload {
  /** Ako izostavljeno, fakturišu se SVE stavke u punoj preostaloj količini. */
  items?: Array<{ itemId: string; quantity: string }>;
  invoiceType?: SalesDocumentType;
  idempotencyKey: string;
}

export interface RegisterSalesDocumentPaymentPayload {
  method: PaymentMethod;
  amount: string;
  note?: string | null;
}

// ---------------------------------------------------------------------------
// Bankovni izvodi i uparivanje uplata
// ---------------------------------------------------------------------------

export const ReconciliationMode = {
  AUTO: "AUTO",
  CONFIRM: "CONFIRM",
  MANUAL: "MANUAL",
} as const;
export type ReconciliationMode =
  (typeof ReconciliationMode)[keyof typeof ReconciliationMode];

export const BankStatementLineDirection = {
  CREDIT: "CREDIT",
  DEBIT: "DEBIT",
} as const;
export type BankStatementLineDirection =
  (typeof BankStatementLineDirection)[keyof typeof BankStatementLineDirection];

export const BankStatementLineStatus = {
  UNMATCHED: "UNMATCHED",
  SUGGESTED: "SUGGESTED",
  BOOKED: "BOOKED",
  IGNORED: "IGNORED",
} as const;
export type BankStatementLineStatus =
  (typeof BankStatementLineStatus)[keyof typeof BankStatementLineStatus];

export type MatchConfidence = "EXACT" | "SUGGESTED";

export interface BankStatementLineDto {
  id: string;
  direction: BankStatementLineDirection;
  amount: string;
  valueDate: string;
  payerName: string | null;
  description: string | null;
  referenceNumber: string | null;
  status: BankStatementLineStatus;
  matchedDocumentId: string | null;
  matchedDocumentNumber?: string | null;
  matchConfidence: MatchConfidence | null;
}

export interface BankStatementDto {
  id: string;
  statementNumber: string | null;
  statementDate: string;
  accountNumber: string | null;
  previousBalance: string;
  newBalance: string;
  fileName: string | null;
  reviewStatus: "PENDING_REVIEW" | "APPROVED";
  lines: BankStatementLineDto[];
  createdAt: string;
}

export interface BankStatementCandidateDto {
  id: string;
  documentNumber: string | null;
  type: SalesDocumentType;
  customerName: string | null;
  dueAmount: string;
  grossAmount: string;
  isSuggested: boolean;
}

export interface BookBankStatementLinePayload {
  documentId: string;
}

// ---------------------------------------------------------------------------
// SEF (eFakture) - produkciona integracija
// ---------------------------------------------------------------------------

export const SefMode = {
  DEMO: "DEMO",
  PROD: "PROD",
} as const;
export type SefMode = (typeof SefMode)[keyof typeof SefMode];

export interface SefSettingsDto {
  sefMode: SefMode;
  sefSendToCir: boolean;
  /** Nikad ne vraćamo pravi ključ kroz API - samo da li je podešen. */
  hasApiKey: boolean;
}

export interface UpdateSefSettingsPayload {
  apiKey?: string;
  sefMode?: SefMode;
  sefSendToCir?: boolean;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export interface DashboardSummaryDto {
  defaultBusinessLocationId: string;
  currentMonthRevenue: string;
  outstandingAmount: string;
  overdueAmount: string;
  todayFiscalTurnover: string;
  eInvoicesPendingDecision: number;
  documentsNeedingAttention: number;
  /** Obračunata potrošnja u periodu (tekući mesec): uvoz bankovnih izvoda (bank_import + api_bank_import). */
  bankImportTotal?: number;
  /** Obračunata potrošnja u periodu: Kasa (osnovna). */
  cashRegisterTotal?: number;
  /** Ukupna obračunata potrošnja svih usluga u periodu ("Dodatna zaduženja"). */
  additionalChargesTotal?: number;
  recentInvoices: FiscalDocumentDto[];
  invoicedVsCollected: {
    month: string;
    invoiced: string;
    collected: string;
  }[];
  bankYear?: number;
  bankYearIncome?: number;
  bankYearExpense?: number;
  bankMonthly?: { month: string; income: number; expense: number }[];
}

// ---------------------------------------------------------------------------
// Request payload oblici (koriste ih i @finora/validation zod šeme i API DTO)
// ---------------------------------------------------------------------------

export interface CreateFiscalDocumentPayload {
  businessLocationId: string;
  type: FiscalDocumentType;
  customerId?: string | null;
  currency?: string;
  note?: string | null;
}

/** Avans-Prodaja (razgovor 23.09.2026) - poseban tok, jedna generička
 * "Аванс" stavka u iznosu stvarno primljenog avansa + odmah registrovano
 * plaćanje, NE stvarni artikli budućeg prometa (vidi
 * FiscalDocumentsService.createAdvanceSale). */
export interface CreateAdvanceSalePayload {
  businessLocationId: string;
  customerId?: string | null;
  buyerPib?: string | null;
  buyerName?: string | null;
  buyerIdType?: string | null;
  buyerAddress?: string | null;
  taxRateId: string;
  amount: string;
  paymentMethod: PaymentMethod;
  receivedAmount?: string;
  payments?: { method: PaymentMethod; amount: string }[];
  note?: string | null;
}

export interface AddFiscalDocumentItemPayload {
  productId?: string | null;
  name: string;
  quantity: string;
  unitPriceGross: string;
  taxRateId: string;
  discountAmount?: string;
}

export interface UpdateFiscalDocumentItemPayload {
  quantity?: string;
  discountAmount?: string;
}

export interface RegisterPaymentPayload {
  method: PaymentMethod;
  amount: string;
  receivedAmount?: string;
}

export interface FiscalizeDocumentPayload {
  /** Klijent generiše idempotency ključ (npr. UUID) da spreči duplu fiskalizaciju. */
  idempotencyKey: string;
}

// ---------------------------------------------------------------------------
// Otpremnica (delivery note) - elektronska potvrda prijema robe
// ---------------------------------------------------------------------------

export const DeliveryNoteStatus = {
  SENT: "SENT",
  CONFIRMED: "CONFIRMED",
} as const;
export type DeliveryNoteStatus = (typeof DeliveryNoteStatus)[keyof typeof DeliveryNoteStatus];

export interface DeliveryNoteDto {
  id: string;
  companyId: string;
  salesDocumentId: string;
  documentNumber: string | null;
  customerName?: string | null;
  issueDate: string;
  publicLinkToken: string;
  status: DeliveryNoteStatus;
  confirmedByName?: string | null;
  confirmedBySurname?: string | null;
  confirmedByIdCardNumber?: string | null;
  confirmedByIdCardIssuePlace?: string | null;
  confirmedByIp?: string | null;
  confirmedNote?: string | null;
  confirmedAt?: string | null;
  createdAt: string;
}

export interface ConfirmDeliveryNotePayload {
  confirmedByName: string;
  confirmedBySurname: string;
  confirmedByIdCardNumber: string;
  confirmedByIdCardIssuePlace: string;
  confirmedNote?: string;
}

export interface PublicDeliveryNoteDto {
  documentNumber: string | null;
  issueDate: string;
  status: DeliveryNoteStatus;
  companyName: string;
  customerName: string | null;
  items: { name: string; quantity: string; unit: string; serialNumber?: string | null }[];
  confirmedByName?: string | null;
  confirmedBySurname?: string | null;
  confirmedAt?: string | null;
}

// ---------------------------------------------------------------------------
// Prijem robe: magacin, dobavljači, ulaz robe, lager
// ---------------------------------------------------------------------------

export interface WarehouseDto {
  id: string;
  name: string;
  isPartOfBusinessLocation: boolean;
  businessLocationId?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  isDefault: boolean;
}

export interface CreateWarehousePayload {
  name: string;
  isPartOfBusinessLocation: boolean;
  businessLocationId?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
}
export type UpdateWarehousePayload = Partial<CreateWarehousePayload>;

export interface SupplierDto {
  id: string;
  companyId: string;
  name: string;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  taxId?: string | null;
  registrationNumber?: string | null;
  bankAccount?: string | null;
  status: EntityLifecycleStatus;
}

export interface CreateSupplierPayload {
  name: string;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  taxId?: string | null;
  registrationNumber?: string | null;
  bankAccount?: string | null;
}
export type UpdateSupplierPayload = Partial<CreateSupplierPayload>;

export const GoodsReceiptStatus = {
  DRAFT: "DRAFT",
  RECEIVED: "RECEIVED",
  CANCELLED: "CANCELLED",
} as const;
export type GoodsReceiptStatus = (typeof GoodsReceiptStatus)[keyof typeof GoodsReceiptStatus];

export interface GoodsReceiptItemDto {
  id: string;
  productId?: string | null;
  sku?: string | null;
  barcode?: string | null;
  serialNumber?: string | null;
  name: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  netAmount: string;
  taxRatePercent: string;
  taxAmount: string;
  expiryDate?: string | null;
}

export interface GoodsReceiptDto {
  id: string;
  companyId: string;
  warehouseId: string;
  warehouseName: string;
  supplierId: string;
  supplierName: string;
  documentNumber?: string | null;
  supplierDeliveryNoteNumber?: string | null;
  receiptDate: string;
  dueDate?: string | null;
  netAmount: string;
  grossAmount: string;
  status: GoodsReceiptStatus;
  items: GoodsReceiptItemDto[];
  createdAt: string;
}

export interface GoodsReceiptItemInput {
  productId?: string | null;
  sku?: string;
  barcode?: string;
  serialNumber?: string;
  name: string;
  quantity: string;
  unitPrice: string;
  discountPercent?: string;
  taxRatePercent?: string;
  expiryDate?: string | null;
}

export interface CreateGoodsReceiptPayload {
  warehouseId: string;
  supplierId: string;
  supplierDeliveryNoteNumber?: string;
  receiptDate?: string;
  dueDate?: string;
  items: GoodsReceiptItemInput[];
}

export interface StockLevelDto {
  productId: string;
  productName: string;
  productSku: string;
  productBarcode?: string;
  productUnit?: string;
  warehouseId: string;
  warehouseName: string;
  quantity: string;
}

// ---------------------------------------------------------------------------
// Autentifikacija i korisnici
// ---------------------------------------------------------------------------

export interface UserDto {
  id: string;
  email: string;
  fullName: string;
  // Alias za fullName - "Dopuna specifikacije" 25.09.2026 (kasa/kiosk
  // ocekuje bas ovo ime polja). Namerno DODATO, ne zamenjuje fullName (vec
  // postojeci web admin panel ga koristi).
  name: string;
  isActive: boolean;
  role: CompanyUserRole;
  lastLoginAt?: string | null;
  createdAt: string;
  isSuperAdmin?: boolean;
  // Da li zaposleni ima AKTIVNU NFC karticu - vidi EmployeeCard /
  // UsersService.assignCard/removeCard. Opciono - create()/update() ga ne
  // popunjavaju (vidi napomenu u users.service.ts).
  cardActive?: boolean;
}

export interface LoginPayload {
  email: string;
  password: string;
}

// Signup od sada NAMERNO ne traži firmu ni lozinku - vidi razgovor "za
// registraciju me nisi poslusao ... samo email i ime". Firma se pravi u
// onboarding "Kompanija" koraku (OnboardingCreateCompanyPayload), lozinka se
// postavlja preko mejla (ConfirmAccountPayload).
export interface SignupPayload {
  fullName: string;
  email: string;
}

export interface SignupResponseDto {
  message: string;
}

export interface ConfirmAccountPayload {
  token: string;
  password: string;
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface MessageResponseDto {
  message: string;
}

// Prijava preko OTP koda na mejl - vidi razgovor "korisnik moze za login da
// bira hoce li otp ili sifru". Alternativa LoginPayload (lozinka).
export interface OtpRequestPayload {
  email: string;
}

export interface OtpVerifyPayload {
  email: string;
  code: string;
}

export interface OnboardingCreateCompanyPayload {
  companyName: string;
  taxId: string;
  registrationNumber: string;
  address: string;
  isVatRegistered?: boolean;
  bankAccounts?: { accountNumber: string; bankName?: string | null }[];
}

export interface OnboardingCreateCompanyResponseDto {
  accessToken: string;
  companyName: string;
}

// Finora Admin -> "Nova firma" - super-admin ručno pravi firmu + vlasnika u
// jednom koraku (puna lozinka, bez mejl-potvrde). NAMERNO odvojeno od
// SignupPayload (javna registracija je od sada samo email+ime) - vidi
// razgovor i SuperAdminService.createCompany.
export interface CreateCompanyAsAdminPayload {
  companyName: string;
  taxId: string;
  registrationNumber: string;
  address: string;
  fullName: string;
  email: string;
  password: string;
  isVatRegistered?: boolean;
  bankAccounts?: { accountNumber: string; bankName?: string | null }[];
}

export interface LoginResponseDto {
  accessToken: string;
  user: UserDto;
  companyName: string;
  // false SAMO odmah nakon confirmAccount - korisnik još nema firmu i
  // onboarding "Kompanija" korak treba da je KREIRA (POST), ne da je edituje
  // (GET/PATCH). Vidi CompanyStep.tsx i auth-store.ts.
  hasCompany: boolean;
}

// ---------------------------------------------------------------------------
// Email template-i i platformski SMTP (Finora Admin -> Podešavanja -> Email,
// "Email Template-i") - vidi razgovor. NAMERNO odvojeno od SmtpSettingsDto
// (to je po-firmi SMTP za slanje faktura kupcima).
// ---------------------------------------------------------------------------
export type EmailTemplateKey = "ACCOUNT_CONFIRMATION" | "ACCOUNT_CONFIRMED" | "PASSWORD_RESET" | "OTP_LOGIN" | "PACKAGE_SHIPPED" | "SHIPMENT_STATUS";

export interface EmailTemplateDto {
  key: EmailTemplateKey;
  subject: string;
  htmlBody: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdateEmailTemplatePayload {
  subject?: string;
  htmlBody?: string;
}

export interface EmailTemplatePreviewDto {
  subject: string;
  html: string;
}

export interface PlatformMailSettingsDto {
  hasSmtpConfig: boolean;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  smtpFrom: string | null;
  smtpFromName: string | null;
  smtpSecure: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdatePlatformMailSettingsPayload {
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPass?: string;
  smtpFrom?: string;
  smtpFromName?: string;
  smtpSecure?: boolean;
}

export interface CreateUserPayload {
  email: string;
  fullName: string;
  password: string;
  role: CompanyUserRole;
}

export interface UpdateUserPayload {
  fullName?: string;
  role?: CompanyUserRole;
  isActive?: boolean;
  password?: string;
}

// ---------------------------------------------------------------------------
// Super Admin (Finora vlasnik - pregled svih firmi na platformi)
// ---------------------------------------------------------------------------

export interface CompanyAdminDto {
  id: string;
  name: string;
  slug: string;
  taxId: string;
  isActive: boolean;
  userCount: number;
  createdAt: string;
  billingGroupId: string | null;
  street?: string | null;
  houseNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  phone?: string | null;
  registrationNumber?: string | null;
  bankAccounts?: { accountNumber: string; bankName: string | null; isDefault: boolean }[];
}

export interface CompanyStatsDto {
  customerCount: number;
  quoteCount: number;
  invoiceCount: number;
  fiscalizedCount: number;
}

/**
 * Super-admin ekran "Logovi" (28.09.2026, korisnicki zahtev - "da mogu da
 * pratim greske po klijentu"). Sva tri reda su cross-company (svi
 * klijenti), za razliku od ostatka aplikacije koji je uvek filtriran po
 * jednoj firmi.
 */
export interface FiscalizationLogEntryDto {
  id: string;
  companyId: string;
  companyName: string;
  fiscalNumber: string | null;
  documentNumber: string | null;
  type: FiscalDocumentType;
  status: FiscalDocumentStatus;
  grossAmount: string;
  currency: string;
  /** Poruka poslednjeg pokusaja fiskalizacije - popunjeno samo kad status nije uspesan. */
  lastErrorMessage: string | null;
  createdAt: string;
}

export interface CloudApiLogEntryDto {
  id: string;
  companyId: string | null;
  companyName: string | null;
  method: string;
  path: string;
  statusCode: number;
  errorCode: string | null;
  errorMessage: string | null;
  ip: string | null;
  createdAt: string;
}

export interface BankStatementImportLogEntryDto {
  id: string;
  companyId: string | null;
  companyName: string | null;
  jobId: string;
  pib: string;
  filename: string | null;
  bank: string | null;
  statementNumber: string | null;
  status: "SUCCESS" | "FAILED";
  errorCode: string | null;
  errorTitle: string | null;
  errorMessage: string | null;
  createdAt: string;
}

/**
 * Tab "Mobile" na super-admin ekranu "Logovi" (29.09.2026, korisnicki zahtev)
 * - svaki zahtev ka Finora Mobile API-ju (/mobile/receipts/*), vidi
 * MobileRequestLoggerMiddleware.
 */
export interface MobileLogEntryDto {
  id: string;
  companyId: string | null;
  companyName: string | null;
  userId: string | null;
  method: string;
  path: string;
  statusCode: number | null;
  errorMessage: string | null;
  ip: string | null;
  createdAt: string;
}

/**
 * Tab "PFR odgovori" na super-admin ekranu "Logovi" (29.09.2026, korisnicki
 * zahtev) - SVAKI poziv ka PFR-u (createInvoice/getStatus/
 * getInvoiceByRequestId/refund/copy), ne samo krajnji rezultat fiskalizacije
 * - vidi PfrFiscalizationProvider.withPfrLogging().
 */
export interface CourierApiLogEntryDto {
  id: string;
  companyId: string | null;
  companyName: string | null;
  courier: string;
  operation: string;
  success: boolean;
  errorMessage: string | null;
  request: string | null;
  response: string | null;
  createdAt: string;
}

export interface SefApiLogEntryDto {
  id: string;
  companyId: string | null;
  companyName: string | null;
  mode: string;
  operation: string;
  success: boolean;
  httpStatus: number | null;
  errorMessage: string | null;
  request: string | null;
  response: string | null;
  createdAt: string;
}

export interface PfrResponseLogEntryDto {
  id: string;
  fiscalDocumentId: string | null;
  companyId: string | null;
  companyName: string | null;
  operation: string;
  requestId: string | null;
  success: boolean;
  statusCode: number | null;
  errorMessage: string | null;
  rawResponse: string | null;
  createdAt: string;
}

export interface SystemApiKeyDto {
  id: string;
  keyPrefix: string;
  label: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface AdminModuleDto {
  id: string;
  key: string;
  parentKey: string | null;
  name: string;
  description: string | null;
  defaultBillingType: string;
  defaultPrice: string;
  isActive: boolean;
}

export interface ModuleGroupDto {
  id: string;
  name: string;
  description: string | null;
  companyCount: number;
  prices: Array<{
    moduleId: string;
    moduleKey: string;
    moduleName: string;
    billingType: string;
    price: string;
  }>;
}

export interface CompanyModuleDto {
  moduleId: string;
  moduleKey: string;
  moduleName: string;
  parentKey: string | null;
  status: string;
  overrideBillingType: string | null;
  overridePrice: string | null;
  effectiveBillingType: string;
  effectivePrice: string;
  trialUntil: string | null;
  licenseCount: number | null;
}

export interface CompanySubscriptionAdminDto {
  subscription: {
    packageId: string;
    packageKey: string;
    packageName: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    trialEndsAt: string | null;
  } | null;
  packages: Array<{
    id: string;
    key: string;
    name: string;
    price: string;
    billingPeriod: string;
  }>;
}

export interface CompanyUsageDto {
  summary: Array<{ moduleKey: string; moduleName: string; quantity: number; totalPrice: number; count: number }>;
  totalBillableUnits: number;
  totalCost: number;
  recentEvents: Array<{
    id: string;
    moduleName: string;
    eventType: string;
    quantity: string;
    unitPrice: string;
    totalPrice: string;
    referenceId: string | null;
    occurredAt: string;
  }>;
}

export interface AdminAuditLogDto {
  id: string;
  adminUserId: string;
  action: string;
  targetType: string;
  targetId: string | null;
  beforeValue: string | null;
  afterValue: string | null;
  createdAt: string;
}

export interface ServiceEndpointDto {
  key: string;
  name: string;
  url: string;
  status: "ENABLED" | "DISABLED";
  timeoutMs: number;
  retryCount: number;
  retryDelayMs: number;
  authHeaderName: string | null;
  authHeaderValueMasked: string | null;
  hasAuthHeaderValue: boolean;
  lastTestedAt: string | null;
  lastTestStatus: string | null;
  lastTestHttpStatus: number | null;
  lastTestResponseMs: number | null;
  lastTestError: string | null;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdateServiceEndpointPayload {
  name?: string;
  url?: string;
  status?: "ENABLED" | "DISABLED";
  timeoutMs?: number;
  retryCount?: number;
  retryDelayMs?: number;
  authHeaderName?: string | null;
  authHeaderValue?: string | null;
}

export interface ModuleDto {
  id: string;
  key: string;
  parentKey: string | null;
  name: string;
  description: string | null;
  defaultBillingType: string;
  defaultPrice: string;
  isActive: boolean;
  addonAvailable: boolean;
  addonPrice: string | null;
  minimumPackage: { id: string; key: string; name: string } | null;
  /** Ključevi modula koji moraju biti aktivni da bi ovaj modul radio (npr. MiniMax zahteva uvoz izvoda). */
  requiredModuleKeys?: string[];
}

export interface PackageDto {
  id: string;
  key: string;
  name: string;
  price: string;
  billingPeriod: "MONTHLY" | "YEARLY";
  trialEnabled: boolean;
  trialDurationDays: number | null;
  includedUsers: number;
  additionalUserPrice: string;
  additionalWarehousePrice: string;
  isActive: boolean;
  sortOrder: number;
  // "Finora ERP" (osnovni paketi) vs "Finora API" (partnerski API pristup) -
  // vidi razgovor 23.09.2026, "checkbox da li je ERP ili je finora api".
  family: "ERP" | "API";
  includedModules: { id: string; key: string; name: string }[];
}

export interface EntitlementsSummaryDto {
  modules: string[];
  // additionalPrice je NETO (bez PDV-a, iz Package.additionalWarehousePrice/
  // additionalUserPrice); additionalPriceGross je "sa PDV-om" cena po stopi
  // MATIČNE Finora firme (izdavaoca ponude) - vidi
  // SubscriptionService.getEntitlements (23.09.2026, "prikaz pdva kada se
  // poručuju paketi").
  warehouses: { included: number; used: number; additionalPrice: string; additionalPriceGross: string };
  users: { included: number; used: number; additionalPrice: string; additionalPriceGross: string } | null;
}

export interface MobileUserAccessDto {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
  mobileAppEnabled: boolean;
}

export interface FcmDeviceDto {
  id: string;
  userName: string;
  userEmail: string;
  platform: "ANDROID" | "IOS" | "WINDOWS" | "LINUX" | "MACOS";
  deviceId: string | null;
  isActive: boolean;
  lastActiveAt: string;
  createdAt: string;
}

/** GET /super-admin/companies/:companyId/fcm-devices/:deviceId - 24.09.2026, "Uredjaji" admin ekran. */
export interface FcmDeviceDetailDto {
  id: string;
  userId: string;
  companyId: string;
  platform: "ANDROID" | "IOS" | "WINDOWS" | "LINUX" | "MACOS";
  deviceId: string | null;
  isActive: boolean;
  lastActiveAt: string;
  createdAt: string;
  updatedAt: string;
  deviceName: string | null;
  appVersion: string | null;
  osVersion: string | null;
  model: string | null;
  language: string | null;
  screenWidth: number | null;
  screenHeight: number | null;
  batteryPercent: number | null;
  isCharging: boolean | null;
  freeStorageMb: number | null;
  totalStorageMb: number | null;
  freeRamMb: number | null;
  totalRamMb: number | null;
  networkType: string | null;
  wifiSsid: string | null;
  signalStrengthDbm: number | null;
  latitude: number | null;
  longitude: number | null;
  deviceTimestamp: string | null;
  uptimeSeconds: number | null;
  launchCount: number | null;
  lastIp?: string | null;
  lpfrState?: string | null;
  lpfrInfo?: Record<string, unknown> | null;
  lpfrCheckedAt?: string | null;
  user: { fullName: string; email: string };
  company: { name: string };
}

export interface DeviceActivityLogDto {
  id: string;
  type: string;
  command: string | null;
  commandId: string | null;
  status: string | null;
  message: string | null;
  createdAt: string;
}

export interface DeviceModuleDto {
  featureKey: string;
  name: string;
  allowed: boolean;
}

export const DEVICE_COMMANDS = [
  "RESTART_SESSION",
  "REFRESH_LICENSE",
  "REFRESH_MODULES",
  "SYNC_PRODUCTS",
  "REQUEST_DIAGNOSTICS",
  "LOCK_REGISTER",
  "LOGOUT_DEVICE",
] as const;
export type DeviceCommand = (typeof DEVICE_COMMANDS)[number];

export interface StatementImportAddressDto {
  id: string;
  companyId: string;
  pib: string;
  emailAddress: string;
  label: string | null;
  status: "ACTIVE" | "REVOKED";
  createdAt: string;
  revokedAt: string | null;
}

export interface StatementProcessingHistoryDto {
  jobId: string;
  companyName: string | null;
  pib: string;
  filename: string | null;
  bank: string | null;
  transactionCount: number | null;
  status: "SUCCESS" | "FAILED";
  errorCode: string | null;
  /** "F" (greška Finore) ili "E" (greška spolja - n8n/banka); `null` kad status nije FAILED. */
  errorId: "F" | "E" | null;
  /** Tačan opis greške (za tooltip); `null` kad nema greške. */
  errorMessage: string | null;
  createdAt: string;
}

export interface MinimaxReportHistoryDto {
  reportDate: string;
  status: string;
  itemCount: number;
  skippedItemCount: number;
  /** Vrednost prometa zaliha (StockEntry, "Sinhronizuj promet"). */
  totalValue: string;
  lastError: string | null;
  postingStatus: string | null;
  postingLastError: string | null;
  /** Ukupan dnevni promet (bruto, sa PDV-om) knjižen u IssuedInvoicePosting ("Knjiži dnevni izveštaj"). */
  postingTotalAmount: string;
}

export interface MinimaxStatusDto {
  connected: boolean;
  status: "PENDING" | "ACTIVE" | "ERROR" | "DISCONNECTED" | null;
  hasClientId: boolean;
  hasClientSecret: boolean;
  organisationId: string | null;
  organisationName: string | null;
  warehouseId: string | null;
  warehouseName: string | null;
  cashPaymentMethodId: string | null;
  cardPaymentMethodId: string | null;
  vatCalculationType: string | null;
  /** Odredišni mejl za "Obrada izvoda" (n8n mail-import XML) - nezavisno od
   * OAuth konekcije, vidi MinimaxConnection.statementEmail. */
  statementEmail: string | null;
  lastError: string | null;
  itemsLastSyncedAt: string | null;
  lastReport: {
    reportDate: string;
    status: string;
    itemCount: number;
    skippedItemCount: number;
    totalValue: string;
    lastError: string | null;
    postingStatus: string | null;
    postingLastError: string | null;
    postingTotalAmount: string;
  } | null;
}

/**
 * Odgovor `GET /services/statement-processing/destination` - "Obrada
 * izvoda" (n8n mail-import), vidi StatementProcessingService.getDestination.
 * Poziva ga ISKLJUČIVO n8n (isti sistemski ključ kao /import i /error), nema
 * frontend potrošača - definisano ovde radi doslednosti sa ostatkom API-ja.
 */
export interface StatementProcessingDestinationDto {
  success: true;
  pib: string;
  company_name: string;
  minimax_enabled: boolean;
  statement_email: string | null;
  /** Tačno onako kako su uneti u Podešavanja -> Firma -> Bankovni računi
   * (CompanyBankAccount.accountNumber), SAMO aktivni računi firme. n8n treba
   * da poredi posle iste normalizacije kao Finora (ukloni razmake/crtice -
   * vidi normalizeAccountNumber u bank-statement-import.service.ts), ne
   * pretpostavljati fiksan format sa crtama. */
  bank_accounts: string[];
}

// ---------------------------------------------------------------------------
// Finora Cloud API (v1) - dashboard administracija integracija/ključeva
// ---------------------------------------------------------------------------

export const ApiIntegrationType = {
  WOOCOMMERCE: "WOOCOMMERCE",
  WHMCS: "WHMCS",
  MAGENTO: "MAGENTO",
  SHOPIFY: "SHOPIFY",
  POS: "POS",
  ERP: "ERP",
  CUSTOM: "CUSTOM",
  BANK_IMPORT: "BANK_IMPORT",
} as const;
export type ApiIntegrationType = (typeof ApiIntegrationType)[keyof typeof ApiIntegrationType];

export const ApiIntegrationStatus = {
  ACTIVE: "ACTIVE",
  DISABLED: "DISABLED",
} as const;
export type ApiIntegrationStatus = (typeof ApiIntegrationStatus)[keyof typeof ApiIntegrationStatus];

export interface CloudApiKeyDto {
  id: string;
  keyPrefix: string;
  /** Pun ključ (null za stare ključeve napravljene pre ove izmene). */
  rawKey?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

export interface CloudApiIntegrationDto {
  id: string;
  companyId: string;
  businessLocationId?: string | null;
  businessLocationName?: string | null;
  name: string;
  type: ApiIntegrationType;
  status: ApiIntegrationStatus;
  createdAt: string;
  apiKeys: CloudApiKeyDto[];
}

export interface CreateCloudApiIntegrationPayload {
  name: string;
  type: ApiIntegrationType;
  businessLocationId?: string | null;
}

export interface CloudApiNewKeyDto {
  id: string;
  keyPrefix: string;
  /** Puna vrednost ključa - vraća se SAMO ovde, jednom. */
  rawKey: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Print Agent - vidi "Finora - Print Agent API (checkin, licenca, lista
// agenata)" specifikaciju. NAMERNO snake_case polja (izuzetak od ostatka
// ovog fajla) - ovo je BUKVALNO ožičenje iz spec-a (Windows agent NE zove
// ove rute, ali ih zove Finora kasa/mobilna - vidi arhitekturni dijagram u
// spec-u tačka 1), pa polja moraju da odgovaraju TAČNO dokumentu, ne
// projektnoj konvenciji.
// ---------------------------------------------------------------------------

export interface PrintAgentPrinterDto {
  name: string;
  default?: boolean;
}

export interface PrintAgentListItemDto {
  id: string;
  agent_id: string;
  name: string;
  hostname: string;
  version: string;
  lan_enabled: boolean;
  urls: string[];
  mdns_host: string | null;
  printers: PrintAgentPrinterDto[];
  default_printer: string | null;
  default_thermal_printer: string | null;
  /** Dodeljena poslovna lokacija (03.10.2026, automatska stampa) - null = nije dodeljena. */
  business_location_id: string | null;
  /** Koji licencni kljuc je agent poslednji put koristio za checkin (04.10.2026, grupisanje u UI). */
  print_agent_key_id: string | null;
  last_seen_at: string;
  online: boolean;
  revoked: boolean;
}

export interface PrintAgentConnectionDto {
  agent_id: string;
  urls: string[];
  mdns_host: string | null;
  /** Tajna - prikazuje se samo kasi koja se upravo poveže, ne u listi. */
  auth_token: string;
}

export interface PrintAgentKeyDto {
  id: string;
  label: string | null;
  key_prefix: string;
  /** Pun ključ (ako je sačuvan); null za stare ključeve napravljene pre ove izmene. */
  key?: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface PrintAgentNewKeyDto {
  id: string;
  /** Puna vrednost ključa - vraća se SAMO ovde, jednom (vidi spec, 8.3). */
  key: string;
  key_prefix: string;
}

/** Super Admin, cross-company (vidi spec 8.4) - za razliku od PrintAgentListItemDto, ovde imamo company_name/company_id, bez urls/printers detalja koji admin-u nisu potrebni na pregledu preko svih firmi. */
export interface PrintAgentAdminListItemDto {
  id: string;
  company_id: string;
  company_name: string;
  agent_id: string;
  name: string;
  hostname: string;
  version: string;
  lan_enabled: boolean;
  last_seen_at: string;
  online: boolean;
  revoked: boolean;
}

export interface PrintAgentAdminKeyDto {
  id: string;
  company_id: string;
  company_name: string;
  label: string | null;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export const WebhookEventType = {
  FISCALIZATION_COMPLETED: "FISCALIZATION_COMPLETED",
  FISCALIZATION_FAILED: "FISCALIZATION_FAILED",
  DOCUMENT_CANCELLED: "DOCUMENT_CANCELLED",
  DOCUMENT_REFUNDED: "DOCUMENT_REFUNDED",
  IPS_PAYMENT_PAID: "IPS_PAYMENT_PAID",
  IPS_PAYMENT_FAILED: "IPS_PAYMENT_FAILED",
} as const;
export type WebhookEventType = (typeof WebhookEventType)[keyof typeof WebhookEventType];

export interface CloudApiWebhookDto {
  id: string;
  companyId: string;
  integrationId?: string | null;
  url: string;
  events: WebhookEventType[];
  status: ApiIntegrationStatus;
  createdAt: string;
}

export interface CreateCloudApiWebhookPayload {
  integrationId?: string | null;
  url: string;
  events: WebhookEventType[];
}

export interface CloudApiNetworkDeviceDto {
  id: string;
  label: string;
  integrationName: string;
  status: string;
  lastKnownIp?: string | null;
  lastHeartbeatAt?: string | null;
  createdAt: string;
}

export interface CompanyBankAccountDto {
  id: string;
  accountNumber: string;
  iban: string | null;
  bankName: string | null;
  currency: string;
  isDefault: boolean;
  isActive: boolean;
}

export interface BusinessLocationOptionDto {
  id: string;
  name: string;
  address: string;
  isDefault: boolean;
  /** Automatska štampa na Print Agent (03.10.2026) - vidi BusinessLocation.autoPrintEnabled u schema.prisma. */
  autoPrintEnabled?: boolean;
  autoPrintPrinterRole?: "thermal" | "a4" | null;
  autoPrintCopies?: number;
  /** Sirina trake kad je autoPrintPrinterRole="thermal" (04.10.2026). */
  autoPrintThermalWidth?: "80mm" | "58mm" | null;
}

// ---------------------------------------------------------------------------
// Izveštaji
// ---------------------------------------------------------------------------

export interface OverdueInvoiceDto {
  id: string;
  documentNumber: string | null;
  customerName: string | null;
  dueDate: string;
  dueAmount: string;
  grossAmount: string;
  daysOverdue: number;
}

export interface BillingUsageServiceDto {
  key: string;
  name: string;
  billingType: string;
  quantity: string;
  totalCost: string;
  eventCount: number;
}

export interface BillingUsageReportDto {
  period: { from: string; to: string };
  services: BillingUsageServiceDto[];
  grandTotal: string;
}

export interface ReportSummaryDto {
  period: { from: string; to: string };
  outputVat: string;
  inputVat: string;
  vatBalance: string;
  turnover: string;
  purchaseNet: string;
  overdueInvoices: OverdueInvoiceDto[];
  overdueTotalAmount: string;
}

export interface ProductImportSummaryDto {
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// ---------------------------------------------------------------------------
// Magacinsko poslovanje: kalkulacija nabavne cene, nalog magacinu
// ---------------------------------------------------------------------------

export const PurchaseCalculationStatus = {
  DRAFT: "DRAFT",
  CALCULATED: "CALCULATED",
  CONFIRMED: "CONFIRMED",
} as const;
export type PurchaseCalculationStatus =
  (typeof PurchaseCalculationStatus)[keyof typeof PurchaseCalculationStatus];

export const CostAllocationMethod = {
  BY_VALUE: "BY_VALUE",
  BY_QUANTITY: "BY_QUANTITY",
} as const;
export type CostAllocationMethod = (typeof CostAllocationMethod)[keyof typeof CostAllocationMethod];

export interface PurchaseCalculationCostDto {
  id: string;
  name: string;
  amount: string;
}

export interface PurchaseCalculationItemDto {
  id: string;
  goodsReceiptItemId: string;
  name: string;
  quantity: string;
  unitPrice: string;
  allocatedCost: string;
  calculatedUnitCost: string;
  marginPercent: string | null;
  taxRatePercent: string;
  sellingPriceGross: string;
  hasProduct: boolean;
}

export interface PurchaseCalculationDto {
  id: string;
  goodsReceiptId: string;
  documentNumber: string | null;
  status: PurchaseCalculationStatus;
  allocationMethod: CostAllocationMethod;
  supplierName: string | null;
  confirmedBy: string | null;
  confirmedAt: string | null;
  additionalCosts: PurchaseCalculationCostDto[];
  totalAdditionalCost: string;
  items: PurchaseCalculationItemDto[];
  createdAt: string;
}

export const WarehouseIssueOrderStatus = {
  PENDING: "PENDING",
  PARTIALLY_ISSUED: "PARTIALLY_ISSUED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
} as const;
export type WarehouseIssueOrderStatus =
  (typeof WarehouseIssueOrderStatus)[keyof typeof WarehouseIssueOrderStatus];

export interface WarehouseIssueOrderItemDto {
  id: string;
  productId: string | null;
  name: string;
  requestedQuantity: string;
  issuedQuantity: string;
  issuedSerialNumbers: string | null;
}

export interface WarehouseIssueOrderDto {
  id: string;
  documentNumber: string | null;
  status: WarehouseIssueOrderStatus;
  salesDocumentId: string;
  salesDocumentNumber: string | null;
  customerName: string | null;
  warehouseName: string;
  items: WarehouseIssueOrderItemDto[];
  createdAt: string;
}

export interface CloudApiActivityLogEntryDto {
  id: string;
  createdAt: string;
  status: string;
  providerMessage?: string | null;
  technicalDetail?: string | null;
  fiscalNumber?: string | null;
  externalId?: string | null;
  integrationName?: string | null;
  fiscalDocumentId: string;
  documentStatus: string;
  grossAmount: string;
}

// --- Modul "Ugovor" (dogovoreno 23.09.2026, vidi ugovor-modul-todo.md) ----

export type ContractPlaceholderType = "text" | "boolean";

export interface ContractPlaceholderDefDto {
  key: string;
  type: ContractPlaceholderType;
  label: string;
}

export interface ContractTemplateDto {
  id: string;
  name: string;
  bodyText: string;
  placeholders: ContractPlaceholderDefDto[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContractSettingsDto {
  id: string;
  companyId: string;
  representativeName: string | null;
  esirApprovalDate: string | null;
  signingPlace: string | null;
  customFields: Record<string, string> | null;
}

/** Globalna podešavanja ugovora (01.10.2026) - zamenjuje ContractSettings PO FIRMI, vidi backend napomenu. */
export interface ContractProviderSettingsDto {
  id: string;
  representativeName: string | null;
  esirApprovalNumber: string | null;
  esirApprovalDate: string | null;
  paymentDueDays: number;
  suspensionDays: number;
  courtCity: string;
  slaPremiumPrice: string;
  contractDiscount12Percent: string;
  contractDiscount24Percent: string;
}

export type ContractDurationOption = "UNDEFINED" | "MONTHS_12" | "MONTHS_24";
export type ContractSlaLevel = "STANDARD" | "PREMIUM";
export type ContractStatus = "AWAITING_UPLOAD" | "PENDING_APPROVAL" | "APPROVED" | "REJECTED";

export interface ContractDocumentDto {
  id: string;
  fileName: string;
  uploadedAt: string;
  /** Base64 data URL - prisutno samo u admin listi/detaljima ugovora (super-admin/contracts/*), ne u tenant DTO-u. */
  fileData?: string;
}

export interface ContractDto {
  id: string;
  companyId: string;
  templateId: string;
  /** Prisutno samo u admin rutama (super-admin/contracts/*) - uključeno preko Prisma `include`. */
  company?: { id: string; name: string };
  template?: { id: string; name: string };
  durationOption: ContractDurationOption;
  slaLevel: ContractSlaLevel;
  status: ContractStatus;
  // Cena Premium podrške SAMO za ovog klijenta (01.10.2026) - null = koristi
  // se globalna ContractProviderSettings.slaPremiumPrice.
  slaPremiumPriceOverride: string | null;
  discountPercentOverride?: string | null;
  acceptedAt: string;
  // NAPOMENA (01.10.2026): nullable - admin "Generiši ugovor" sad kreira
  // stvaran Contract red PRE nego što klijent unese podatke sa lične karte.
  signatoryIdCardNumber: string | null;
  signatoryIdCardIssuePlace: string | null;
  emailSentAt: string | null;
  uploadDeadlineAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
  documents: ContractDocumentDto[];
  createdAt: string;
}

export interface AcceptContractPayload {
  templateId: string;
  durationOption: ContractDurationOption;
  slaLevel: ContractSlaLevel;
  signatoryIdCardNumber: string;
  signatoryIdCardIssuePlace: string;
}

export interface ContractDiscountedPriceDto {
  regularPrice: string;
  discountedPrice: string;
  percent: number;
}

export type BackupDestinationType = "LOCAL" | "SFTP" | "FTP" | "S3";
// Prosiren state machine (26.09.2026, prepisan modul sa svih 11 sigurnosnih
// ispravki - vidi claude/bekap-baze-modul.md) - backup NIKAD nije SUCCESS
// dok dump+upload+restorability provera nisu svi potvrdjeni.
export type BackupRunStatus = "PENDING" | "DUMPING" | "UPLOADING" | "VERIFYING" | "SUCCESS" | "FAILED";
export type BackupRunTrigger = "SCHEDULE" | "MANUAL";

export interface BackupDestinationDto {
  id: string;
  name: string;
  type: BackupDestinationType;
  isActive: boolean;
  host: string | null;
  port: number | null;
  remotePath: string | null;
  username: string | null;
  bucket: string | null;
  region: string | null;
  endpoint: string | null;
  forcePathStyle: boolean | null;
  // Nikad ciphertext/plaintext - samo da li je secret podesen (tacka 1).
  hasSecret: boolean;
  cronExpression: string;
  retentionCount: number;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BackupDestinationsListDto {
  destinations: BackupDestinationDto[];
  // Tacka 8: upozorenje ako nijedno aktivno odrediste nije van produkcionog
  // servera.
  warnings: string[];
}

export interface BackupRunDto {
  id: string;
  destinationId: string;
  status: BackupRunStatus;
  trigger: BackupRunTrigger;
  triggeredByUserId: string | null;
  startedAt: string;
  finishedAt: string | null;
  sizeBytes: number | null;
  checksumSha256: string | null;
  durationMs: number | null;
  remotePath: string | null;
  errorMessage: string | null;
  restorabilityVerifiedAt: string | null;
  restorabilityCheckOk: boolean | null;
  // Tacka 2/6/9: cisto informativna oznaka, NIKAD ne trigeruje automatsko
  // brisanje - stvarno brisanje ide iskljucivo preko eksplicitne DELETE
  // rute, pojedinacno, sa guard-om protiv brisanja poslednjeg validnog runa.
  retentionCandidate: boolean;
  deletedAt: string | null;
  deletedByUserId: string | null;
  createdAt: string;
}

export interface BackupDestinationPayload {
  name: string;
  type: BackupDestinationType;
  isActive?: boolean;
  host?: string | null;
  port?: number | null;
  remotePath?: string | null;
  username?: string | null;
  bucket?: string | null;
  region?: string | null;
  endpoint?: string | null;
  forcePathStyle?: boolean | null;
  // Plaintext SAMO u ovom payload-u (HTTPS, admin->server) - server ga
  // enkriptuje odmah pre upisa, nikad se ne vraca nazad u odgovoru.
  secret?: string | null;
  cronExpression: string;
  retentionCount?: number;
}

export interface PlatformUserAdminDto {
  id: string;
  email: string;
  fullName: string;
  verified: boolean;
  isActive: boolean;
  isSuperAdmin: boolean;
  companyNames: string[];
  lastLoginAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Shopify integracija (28.09.2026) - vidi docs/shopify-integracija.md
// ---------------------------------------------------------------------------

export type ShopifyPaymentMethod = "BANK_TRANSFER" | "CARD" | "INSTANT_PAYMENT" | "CASH";

export type ShopifyOrderSyncStatus =
  | "PENDING"
  | "WAITING_FULFILLMENT"
  | "FISCALIZED"
  | "BLOCKED_UNMAPPED_PAYMENT"
  | "BLOCKED_LICENSE"
  | "BLOCKED_VALIDATION"
  | "FAILED_RETRYING"
  | "FAILED_FINAL"
  | "SKIPPED"
  | "REFUNDED"
  | "REFUNDED_PARTIAL";

export interface ShopifyStoreDto {
  id: string;
  shopDomain: string;
  shopName: string | null;
  connected: boolean;
  installedAt: string;
  uninstalledAt: string | null;
  scopes: string;
  webhooksRegisteredAt: string | null;
  lastError: string | null;
  businessLocationId: string | null;
  integrationId: string;
  licenseActive: boolean;
  licenseMessage: string | null;
  orderCounts: Partial<Record<ShopifyOrderSyncStatus, number>>;
  /** Prodavnica je odobrila write_customers (polja kupca hafiscal.* i upis u profil kupca). */
  customerFieldsScope: boolean;
}

export interface ShopifyStoreSettingsDto {
  paymentMap: Record<string, ShopifyPaymentMethod | null>;
  pibSources: {
    /** HAfiscal polja: atributi hafiscal_* i metafield-i kupca hafiscal.* (01.10.2026). */
    hafiscalFields: boolean;
    b2bCompany: boolean;
    attributeKey: string | null;
    companyAttributeKey: string | null;
    customerMetafield: string | null;
  };
  rules: {
    legalEntityFiscalReceipt: boolean;
    physicalPersonFiscalReceipt: boolean;
    codOnFulfilled: boolean;
    skipTestOrders: boolean;
    sendReceiptEmail: boolean;
    saveCustomerFields: boolean;
    defaultTaxRate: string;
  };
}

export interface ShopifySettingsResponseDto {
  storeId: string;
  shopDomain: string;
  settings: ShopifyStoreSettingsDto;
  gateways: Array<{
    key: string;
    name: string;
    lastSeenAt: string | null;
    mappedTo: ShopifyPaymentMethod | null;
    suggestion: ShopifyPaymentMethod | null;
  }>;
  businessLocationId: string | null;
  licenseActive: boolean;
  efakturaSupported: boolean;
  requeued?: number;
}

export interface ShopifySettingsUpdatePayload {
  paymentMap?: Record<string, ShopifyPaymentMethod | null>;
  pibSources?: Partial<ShopifyStoreSettingsDto["pibSources"]>;
  rules?: Partial<ShopifyStoreSettingsDto["rules"]>;
  businessLocationId?: string | null;
}

export interface ShopifyOrderSyncDto {
  id: string;
  shopifyOrderId: string;
  orderName: string | null;
  status: ShopifyOrderSyncStatus;
  fiscalDocumentId: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  totalAmount: string | null;
  currency: string | null;
  buyerPib: string | null;
  fiscalizedAt: string | null;
  writebackStatus: "NONE" | "PENDING" | "DONE" | "FAILED";
  writebackError: string | null;
  receiptEmailSentAt: string | null;
  /** Broj ponovo izdatih računa posle refundacija (storno + nov račun, 01.10.2026). */
  reissueCount: number;
  fiscalHistory: Array<{
    documentId: string;
    fiscalNumber: string | null;
    stornoFiscalNumber: string | null;
    refundId: string;
    at: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface ShopifyRefundSyncDto {
  id: string;
  shopifyOrderId: string;
  shopifyRefundId: string;
  status: "PENDING" | "WAITING_ORIGINAL" | "DONE" | "NEEDS_MANUAL" | "FAILED_RETRYING" | "SKIPPED";
  amount: string | null;
  /** Iznos novog računa za ono što kupac zadržava (0.00 = samo storno). */
  remainingAmount: string | null;
  newDocumentId: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface ShopifyOrdersResponseDto {
  total: number;
  items: ShopifyOrderSyncDto[];
  refunds: ShopifyRefundSyncDto[];
}

// ---------------------------------------------------------------------------
// MerchantPro integracija (02.10.2026) - ekran u web panelu. Vidi
// docs/timovi/integracije-merchantpro-povezivanje.md i
// apps/api/src/modules/merchantpro/merchantpro-admin.controller.ts.
// ---------------------------------------------------------------------------

export type MerchantProOrderSyncStatus =
  | "PENDING"
  | "FISCALIZED"
  | "BLOCKED_LICENSE"
  | "BLOCKED_VALIDATION"
  | "FAILED_RETRYING"
  | "FAILED_FINAL"
  | "SKIPPED"
  | "REFUNDED"
  | "REFUND_FAILED";

export interface MerchantProStoreDto {
  id: string;
  siteUrl: string;
  active: boolean;
  apiKeyMasked: string;
  integrationId: string;
  integrationStatus: string;
  businessLocationId: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  licenseActive: boolean;
  licenseMessage: string | null;
  orderCounts: Partial<Record<MerchantProOrderSyncStatus, number>>;
  webhookConfigured: boolean;
}

// Webhook "order.payment_completed" (03.10.2026) - realtime okidač umesto
// cekanja na cron, plus admin_comments ("s/n:<broj ili x>|email/stampa") kao
// kanal za serijski broj i izbor stampa/mejl po porudzbini.
export type MerchantProWebhookInfoDto =
  | { configured: false }
  | { configured: true; url: string; payload: string; headers: string; token: string };

export interface CreateMerchantProStorePayload {
  siteUrl: string;
  apiKey: string;
  apiSecret: string;
  businessLocationId?: string | null;
}

export interface UpdateMerchantProStorePayload {
  siteUrl?: string;
  apiKey?: string;
  apiSecret?: string;
}

export interface MerchantProOrderSyncDto {
  id: string;
  merchantProOrderId: string;
  orderNumber: string | null;
  status: MerchantProOrderSyncStatus;
  fiscalDocumentId: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  totalAmount: string | null;
  currency: string | null;
  buyerPib: string | null;
  equipmentSerialNumber: string | null;
  fiscalizedAt: string | null;
  writebackStatus: "NONE" | "PENDING" | "DONE" | "FAILED";
  writebackError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MerchantProProductMappingDto {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  unitPriceGross: string;
  merchantProProductId: number;
  merchantProVariantId: number | null;
  merchantProSku: string;
  syncStock: boolean;
  syncPrice: boolean;
  lastSyncedAt: string;
  lastPushError: string | null;
}

export interface MerchantProImportProductsResultDto {
  created: number;
  linked: number;
  alreadyMapped: number;
  skippedNoTaxRate: number;
}

export interface UpdateMerchantProProductMappingPayload {
  syncStock?: boolean;
  syncPrice?: boolean;
}

export interface MerchantProOrdersResponseDto {
  total: number;
  items: MerchantProOrderSyncDto[];
}

// --- Modul "Troškovi" (dogovoreno 27.09.2026) -------------------------------

export const ExpenseSource = {
  MANUAL: "MANUAL",
  MOBILE_QR: "MOBILE_QR",
  SEF_INVOICE: "SEF_INVOICE",
} as const;
export type ExpenseSource = (typeof ExpenseSource)[keyof typeof ExpenseSource];

export const ExpenseReportDestination = {
  ACCOUNTANT_EMAIL: "ACCOUNTANT_EMAIL",
  MINIMAX: "MINIMAX",
} as const;
export type ExpenseReportDestination =
  (typeof ExpenseReportDestination)[keyof typeof ExpenseReportDestination];

// Stavka troška (dogovoreno 30.09.2026 - "stavka kolicina cena ukupno...
// koliki je pdv") - isti oblik kao FiscalDocumentItemDto/SalesDocumentItemDto
// (taxRatePercent je DENORMALIZOVAN procenat u trenutku unosa, ne taxRateId -
// isti obrazac, vidi FiscalDocumentItemDto).
export interface ExpenseItemDto {
  id: string;
  name: string;
  quantity: string;
  unitPriceGross: string;
  taxRatePercent: string;
  taxLabel?: string | null;
  netAmount: string;
  taxAmount: string;
  grossAmount: string;
}

export interface ExpenseDto {
  id: string;
  source: ExpenseSource;
  date: string;
  supplierName: string | null;
  supplierPib: string | null;
  // Ukupan bruto (sa PDV-om) iznos - zbir items[].grossAmount.
  amount: string;
  // Zbir items[].netAmount/taxAmount - dodato 30.09.2026 uz stavke, da bi
  // se PDV mogao koristiti kao odbitna stavka kad je firma u sistemu PDV-a.
  netAmount: string;
  taxAmount: string;
  items: ExpenseItemDto[];
  // Slobodna napomena - NIJE VIŠE glavni unos (to su sada items), zadržano
  // radi kompatibilnosti sa ranije unetim troškovima i kao opciono polje.
  description: string | null;
  attachmentUrl: string | null;
  fiscalNumber: string | null;
  verificationUrl: string | null;
  reportedAt: string | null;
  createdAt: string;
}

export interface ExpenseSettingsDto {
  enabled: boolean;
  destination: ExpenseReportDestination;
  accountantEmail: string | null;
  // Uvek false u ovoj fazi - MiniMax odredište je samo podešavanje, stvarno
  // slanje NIJE implementirano (dogovoreno 27.09.2026). Frontend ovim poljem
  // prikazuje "uskoro" umesto da tvrdi da opcija radi.
  minimaxAvailable: boolean;
  lastSentAt: string | null;
}

// Payload oblici za @finora/api-client (Web UI za Troškove, 30.09.2026) -
// polja odgovaraju createExpenseSchema/updateExpenseSettingsSchema u
// @finora/validation (koje frontend koristi za client-side validaciju).
export interface CreateExpenseItemPayload {
  name: string;
  quantity: string;
  unitPriceGross: string;
  // Tačno jedno od ova tri (vidi expenseItemInputSchema u
  // @finora/validation / docs/timovi/troskovi-pdv-sa-racuna.md, 01.10.2026):
  // taxRateId za ručni unos (Web UI bira postojeću stopu firme),
  // taxRatePercent/taxAmount za stavku sa skeniranog računa koja možda nema
  // odgovarajuću stopu kod firme.
  taxRateId?: string;
  taxRatePercent?: string;
  taxAmount?: string;
}
export interface CreateExpensePayload {
  date: string;
  supplierName?: string | null;
  supplierPib?: string | null;
  items: CreateExpenseItemPayload[];
  description?: string | null;
  attachmentUrl?: string | null;
}
export type UpdateExpensePayload = Partial<CreateExpensePayload>;

export interface UpdateExpenseSettingsPayload {
  enabled?: boolean;
  destination?: ExpenseReportDestination;
  accountantEmail?: string;
}

// --- Podrška / tiketi (30.09.2026) ------------------------------------------
export const SupportTicketStatus = {
  OPEN: "OPEN",
  IN_PROGRESS: "IN_PROGRESS",
  RESOLVED: "RESOLVED",
  CLOSED: "CLOSED",
} as const;
export type SupportTicketStatus = (typeof SupportTicketStatus)[keyof typeof SupportTicketStatus];

export const SupportTicketPriority = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
} as const;
export type SupportTicketPriority = (typeof SupportTicketPriority)[keyof typeof SupportTicketPriority];

export const SupportTicketAuthorType = {
  CUSTOMER: "CUSTOMER",
  ADMIN: "ADMIN",
} as const;
export type SupportTicketAuthorType = (typeof SupportTicketAuthorType)[keyof typeof SupportTicketAuthorType];

export interface SupportTicketMessageDto {
  id: string;
  authorType: SupportTicketAuthorType;
  authorName: string | null;
  message: string;
  createdAt: string;
}

export interface SupportTicketDto {
  id: string;
  companyId: string;
  // Popunjeno samo u admin (cross-tenant) prikazu - tenant strana zna svoju
  // firmu iz konteksta, ne treba joj naziv na svakom tiketu.
  companyName?: string;
  subject: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  category: string | null;
  lastMessageAt: string;
  closedAt: string | null;
  createdAt: string;
  // Popunjeno samo pri učitavanju DETALJA jednog tiketa, ne u listi.
  messages?: SupportTicketMessageDto[];
}
